/**
 * `shipments/{id}` (nueva, sección 7) y `carriers/{id}` (sección 8).
 * Los transportistas son DATOS: capacidad = adapterType (código, 5 tipos); configuración = documento.
 */
import { z } from 'zod';
import {
  CarrierIdSchema,
  CountryCodeSchema,
  CurrencySchema,
  LocalizedTextSchema,
  MoneySchema,
  OrderIdSchema,
  ShipmentIdSchema,
  TimestampSchema,
  UserIdSchema,
} from './primitives.js';

export const ADAPTER_TYPES = [
  'packeta',
  'sendcloud',
  'eurosender',
  'generic-rest',
  'manual-tracking-only',
] as const;
export const AdapterTypeSchema = z.enum(ADAPTER_TYPES);
export type AdapterType = z.infer<typeof AdapterTypeSchema>;

export const CARRIER_STATUSES = ['draft', 'shadow', 'active', 'paused', 'retired'] as const;

export const SERVICE_LEVELS = ['locker', 'pickup_point', 'home'] as const;
export const ServiceLevelSchema = z.enum(SERVICE_LEVELS);

export const CorridorSchema = z.object({
  from: CountryCodeSchema,
  to: CountryCodeSchema,
  service_level: ServiceLevelSchema,
  enabled: z.boolean(),
  /** Aduana (CH/NO/IS/LI): solo si el adaptador soporta CN22/CN23 (sección 13b). */
  customs_supported: z.boolean(),
  /** Plazo típico en días naturales. */
  sla_days: z.number().int().min(1).max(60),
});
export type Corridor = z.infer<typeof CorridorSchema>;

/** Tramo de precio: peso máximo (g) × corredor → precio. `margin` se suma encima. */
export const PriceTierSchema = z.object({
  max_weight_grams: z.number().int().positive(),
  from: CountryCodeSchema,
  to: CountryCodeSchema,
  service_level: ServiceLevelSchema,
  price: MoneySchema,
  insurance_included: MoneySchema.nullable(),
  max_dimensions_cm: z.tuple([z.number(), z.number(), z.number()]).nullable(),
});

export const CarrierSchema = z.object({
  id: CarrierIdSchema,
  display_name: LocalizedTextSchema,
  logo_url: z.string().url().nullable(),
  status: z.enum(CARRIER_STATUSES),
  adapter_type: AdapterTypeSchema,
  /** Config específica del adaptador (ids de método, addressId, serviceType…); la valida el adaptador. */
  adapter_config: z.record(z.string(), z.unknown()),
  /** Nombre del secreto en Secret Manager. NUNCA el secreto. */
  credentials_ref: z.string().min(1).nullable(),
  corridors: z.array(CorridorSchema),
  pickup: z.object({
    mode: z.enum(['none', 'optional', 'required']),
    widget_provider: z.enum(['packeta', 'sendcloud', 'none']),
  }),
  price_matrix: z.object({
    currency: CurrencySchema,
    tiers: z.array(PriceTierSchema),
    /** Margen de Nuvvu en céntimos, se suma a cada tramo. */
    margin: MoneySchema,
    surcharges: z.array(
      z.object({ code: z.string(), amount: MoneySchema, label: LocalizedTextSchema }),
    ),
  }),
  label: z.object({
    format: z.enum(['pdf', 'zpl']),
    size: z.enum(['A4', 'A6', '100x150']),
    validity_days: z.number().int().positive(),
    /** QR sin impresora en lockers (sección 10, #5). */
    qr_supported: z.boolean(),
  }),
  tracking: z.object({
    url_template: z.string().nullable(),
    source: z.enum(['adapter_webhook', 'adapter_poll', 'aggregator']),
    /** Mapa estado externo → estado Nuvvu del envío. */
    status_map: z.record(
      z.string(),
      z.enum([
        'label_ready',
        'shipped',
        'in_transit',
        'out_for_delivery',
        'delivered',
        'returned',
        'exception',
      ]),
    ),
  }),
  webhook: z
    .object({
      path: z.string(),
      signature: z.enum(['hmac_sha256', 'bearer', 'none']),
      secret_ref: z.string().nullable(),
    })
    .nullable(),
  ux: z.object({
    sort_order: z.number().int(),
    badge: LocalizedTextSchema.nullable(),
    excluded_category_ids: z.array(z.string()),
  }),
  audit: z.object({
    version: z.number().int().min(1),
    updated_by: UserIdSchema,
    updated_at: TimestampSchema,
  }),
  created_at: TimestampSchema,
});
export type Carrier = z.infer<typeof CarrierSchema>;
export const carrierPath = (id: string) => `carriers/${id}` as const;

// ---------- shipments ----------

export const SHIPMENT_STATUSES = [
  'pending_label',
  'label_ready',
  'shipped',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'returned',
  'exception',
  'cancelled',
] as const;
export const ShipmentStatusSchema = z.enum(SHIPMENT_STATUSES);
export type ShipmentStatus = z.infer<typeof ShipmentStatusSchema>;

export const ShipmentEventSchema = z.object({
  at: TimestampSchema,
  status: ShipmentStatusSchema,
  external_status: z.string().max(120).nullable(),
  location: z.string().max(200).nullable(),
  source: z.enum(['adapter_webhook', 'adapter_poll', 'aggregator', 'manual', 'buyer']),
  raw_ref: z.string().nullable(),
});

export const ShipmentSchema = z.object({
  id: ShipmentIdSchema,
  order_id: OrderIdSchema,
  seller_id: UserIdSchema,
  buyer_id: UserIdSchema,
  carrier_id: CarrierIdSchema,
  adapter_type: AdapterTypeSchema,
  status: ShipmentStatusSchema,
  tracking_number: z.string().nullable(),
  tracking_url: z.string().url().nullable(),
  label_url: z.string().url().nullable(),
  label_expires_at: TimestampSchema.nullable(),
  /** Id del envío en el proveedor (parcel id de Sendcloud, packet id de Packeta…). */
  external_id: z.string().nullable(),
  weight_grams: z.number().int().min(0),
  price: MoneySchema,
  events: z.array(ShipmentEventSchema),
  /** SLA del corredor + 7 días: si no hay entrega, incidencia automática (7b). */
  sla_breach_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Shipment = z.infer<typeof ShipmentSchema>;
export const shipmentPath = (id: string) => `shipments/${id}` as const;
